import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Plus, X, DollarSign, CheckCircle, Clock, Briefcase,
  TrendingUp, AlertCircle, Pause, Play, XCircle
} from "lucide-react";
import { fmtUSD } from "../../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };

const STATUS_COLORS = {
  open: { bg: "bg-emerald-100", text: "text-emerald-700" },
  funded: { bg: "bg-gold/20", text: "text-[#8B6914]" },
  closed: { bg: "bg-gray-100", text: "text-gray-600" },
  paused: { bg: "bg-amber-100", text: "text-amber-700" },
  pending: { bg: "bg-blue-100", text: "text-blue-700" },
  confirmed: { bg: "bg-emerald-100", text: "text-emerald-700" },
  withdrawn: { bg: "bg-red-100", text: "text-red-600" },
};

function InvestorsAdminPanel() {
  const [opps, setOpps] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investor/opportunities`, { headers });
      setOpps(res.data.opportunities || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateOppStatus = async (oppId, status) => {
    try {
      await axios.put(`${API}/investor/opportunities/${oppId}/status?status=${status}`, {}, { headers });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const updateCommitStatus = async (commitId, status) => {
    try {
      await axios.put(`${API}/investor/commitments/${commitId}/status`, { status }, { headers });
      fetchData();
      if (selectedOpp) {
        const res = await axios.get(`${API}/investor/opportunities/${selectedOpp}`, { headers });
        setSelectedOpp(null);
        setTimeout(() => setSelectedOpp(res.data.opportunity.opportunity_id), 100);
      }
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>;

  const totalRaised = opps.reduce((s, o) => s + (o.raised_amount || 0), 0);
  const activeOpps = opps.filter(o => o.status === "open");

  return (
    <div data-testid="investors-admin-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total Opportunities", value: opps.length, color: "text-blue-600" },
          { label: "Active (Open)", value: activeOpps.length, color: "text-emerald-600" },
          { label: "Total Raised", value: fmtUSD(totalRaised), color: "text-gold-dark" },
          { label: "Total Investors", value: opps.reduce((s, o) => s + (o.investor_count || 0), 0), color: "text-purple-600" },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-cream-warm bg-white p-4" data-testid={`investor-admin-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-[#888] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-bold text-[#444]">Investment Opportunities</h3>
        <button onClick={() => setShowCreate(true)} data-testid="investor-create-opp-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 transition-colors">
          <Plus className="h-4 w-4" /> Open Deal for Investment
        </button>
      </div>

      {/* Opportunities Table */}
      <div className="rounded-xl border border-cream-warm bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-cream border-b border-cream-warm">
              <th className="text-left px-4 py-3 font-semibold text-[#444]">Deal</th>
              <th className="text-left px-4 py-3 font-semibold text-[#444]">Type</th>
              <th className="text-right px-4 py-3 font-semibold text-[#444]">Target</th>
              <th className="text-right px-4 py-3 font-semibold text-[#444]">Raised</th>
              <th className="text-center px-4 py-3 font-semibold text-[#444]">Investors</th>
              <th className="text-center px-4 py-3 font-semibold text-[#444]">Yield</th>
              <th className="text-center px-4 py-3 font-semibold text-[#444]">Status</th>
              <th className="text-right px-4 py-3 font-semibold text-[#444]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {opps.map(opp => {
              const snap = opp.deal_snapshot || {};
              const pct = opp.target_raise ? Math.round((opp.raised_amount / opp.target_raise) * 100) : 0;
              const sc = STATUS_COLORS[opp.status] || STATUS_COLORS.closed;
              return (
                <tr key={opp.opportunity_id} className="border-b border-cream-warm hover:bg-cream/50 cursor-pointer"
                  onClick={() => setSelectedOpp(opp.opportunity_id)} data-testid={`opp-row-${opp.opportunity_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0A0A0A] text-xs">{snap.property_address || "—"}</div>
                    <div className="text-[10px] text-[#888]">{snap.state}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{opp.loan_type_label || LOAN_LABELS[snap.loan_type] || "—"}</td>
                  <td className="px-4 py-3 text-right text-xs font-semibold">{fmtUSD(opp.target_raise)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="text-xs font-semibold text-emerald-600">{fmtUSD(opp.raised_amount)}</div>
                    <div className="text-[10px] text-[#888]">{pct}%</div>
                  </td>
                  <td className="px-4 py-3 text-center text-xs font-semibold">{opp.investor_count}</td>
                  <td className="px-4 py-3 text-center text-xs font-bold text-gold-dark">{opp.projected_yield ? `${opp.projected_yield}%` : "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sc.bg} ${sc.text}`}>{opp.status.toUpperCase()}</span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {opp.status === "open" && (
                        <button onClick={() => updateOppStatus(opp.opportunity_id, "paused")}
                          className="p-1.5 rounded-lg text-[#888] hover:text-amber-600 hover:bg-amber-50" title="Pause">
                          <Pause className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {opp.status === "paused" && (
                        <button onClick={() => updateOppStatus(opp.opportunity_id, "open")}
                          className="p-1.5 rounded-lg text-[#888] hover:text-emerald-600 hover:bg-emerald-50" title="Reopen">
                          <Play className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {opp.status !== "closed" && (
                        <button onClick={() => updateOppStatus(opp.opportunity_id, "closed")}
                          className="p-1.5 rounded-lg text-[#888] hover:text-red-600 hover:bg-red-50" title="Close">
                          <XCircle className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {opps.length === 0 && (
              <tr><td colSpan={8} className="text-center py-12 text-sm text-[#888]">No investment opportunities yet. Open a deal for investment to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {showCreate && <CreateOpportunityModal headers={headers} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); }} />}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedOpp && (
          <OppDetailAdmin oppId={selectedOpp} headers={headers}
            onClose={() => setSelectedOpp(null)}
            onUpdate={fetchData}
            onCommitStatusChange={updateCommitStatus} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Create Opportunity Modal ────────────────────────────────
function CreateOpportunityModal({ headers, onClose, onCreated }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [form, setForm] = useState({ target_raise: "", min_investment: "25000", projected_yield: "", projected_term_months: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/lender-crm/available-deals/list`, { headers })
      .then(r => setDeals(r.data.deals || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!selectedDeal || !form.target_raise) return;
    setSaving(true);
    try {
      await axios.post(`${API}/investor/opportunities/create`, {
        lead_id: selectedDeal,
        target_raise: parseFloat(form.target_raise),
        min_investment: parseFloat(form.min_investment) || 25000,
        projected_yield: form.projected_yield ? parseFloat(form.projected_yield) : null,
        projected_term_months: form.projected_term_months ? parseInt(form.projected_term_months) : null,
        notes: form.notes || null,
      }, { headers });
      onCreated();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="create-opp-modal">
        <div className="px-4 sm:px-6 py-4 border-b border-cream-warm flex items-center justify-between">
          <h3 className="text-base font-bold text-[#0A0A0A]">Open Deal for Investment</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-cream"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-[#444] block mb-2">Select Deal *</label>
            {loading ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : (
              <div className="space-y-2 max-h-40 overflow-y-auto" data-testid="create-opp-deal-list">
                {deals.map(d => (
                  <button key={d.lead_id} onClick={() => setSelectedDeal(d.lead_id)}
                    className={`w-full text-left p-3 rounded-xl border transition-colors ${
                      selectedDeal === d.lead_id ? "border-emerald-500 bg-emerald-50" : "border-cream-warm bg-cream hover:border-emerald-300"
                    }`}>
                    <div className="text-xs font-medium text-[#0A0A0A]">{d.property_address || "Deal"}</div>
                    <div className="text-[10px] text-[#888]">
                      {LOAN_LABELS[d.loan_type] || d.loan_type} &bull; {d.state}
                      {d.purchase_price && <> &bull; ${parseInt(d.purchase_price).toLocaleString()}</>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Target Raise ($) *</label>
              <input type="number" value={form.target_raise} onChange={e => setForm(p => ({ ...p, target_raise: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none"
                placeholder="500000" data-testid="create-opp-target" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Min Investment ($)</label>
              <input type="number" value={form.min_investment} onChange={e => setForm(p => ({ ...p, min_investment: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none"
                placeholder="25000" data-testid="create-opp-min" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Projected Yield (%)</label>
              <input type="number" step="0.1" value={form.projected_yield} onChange={e => setForm(p => ({ ...p, projected_yield: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none"
                placeholder="12.5" data-testid="create-opp-yield" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#444] block mb-1">Term (months)</label>
              <input type="number" value={form.projected_term_months} onChange={e => setForm(p => ({ ...p, projected_term_months: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none"
                placeholder="12" data-testid="create-opp-term" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#444] block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none resize-none h-16"
              placeholder="Deal highlights for investors..." data-testid="create-opp-notes" />
          </div>
        </div>

        <div className="px-4 sm:px-6 py-4 border-t border-cream-warm flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-cream-warm text-sm text-[#666]">Cancel</button>
          <button onClick={handleCreate} disabled={saving || !selectedDeal || !form.target_raise}
            data-testid="create-opp-submit"
            className="px-5 py-2 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50 transition-colors flex items-center gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Open for Investment
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Opportunity Detail (Admin View) ─────────────────────────
function OppDetailAdmin({ oppId, headers, onClose, onUpdate, onCommitStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/investor/opportunities/${oppId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [oppId]);

  if (loading || !data) return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
    </motion.div>
  );

  const opp = data.opportunity;
  const commitments = data.commitments || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()} data-testid="opp-detail-admin">
        <div className="px-4 sm:px-6 py-4 border-b border-cream-warm flex items-start justify-between">
          <div>
            <h3 className="text-base font-bold text-[#0A0A0A]">{(opp.deal_snapshot || {}).property_address || "Opportunity"}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-700">
                {opp.loan_type_label || LOAN_LABELS[(opp.deal_snapshot || {}).loan_type]}
              </span>
              {opp.projected_yield && <span className="text-xs font-bold text-gold-dark">{opp.projected_yield}% yield</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-cream"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-5">
          {/* Raise Status */}
          <div className="p-4 rounded-xl bg-cream border border-cream-warm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">${parseInt(opp.raised_amount).toLocaleString()} raised</span>
              <span className="text-sm text-[#888]">of ${parseInt(opp.target_raise).toLocaleString()}</span>
            </div>
            <div className="h-3 rounded-full bg-white overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, (opp.raised_amount / opp.target_raise) * 100)}%` }} />
            </div>
            <div className="text-xs text-[#888] mt-2">{opp.investor_count} investor(s)</div>
          </div>

          {/* Commitments Table */}
          <div>
            <h4 className="text-xs font-bold text-[#444] mb-3">Capital Commitments ({commitments.length})</h4>
            {commitments.length === 0 ? (
              <div className="text-center py-8 text-sm text-[#888] bg-cream rounded-xl border border-cream-warm">No commitments yet</div>
            ) : (
              <div className="space-y-2">
                {commitments.map(c => {
                  const sc = STATUS_COLORS[c.status] || STATUS_COLORS.pending;
                  return (
                    <div key={c.commitment_id} className="flex items-center justify-between p-3 rounded-xl border border-cream-warm bg-white"
                      data-testid={`admin-commit-${c.commitment_id}`}>
                      <div>
                        <div className="text-sm font-semibold text-[#0A0A0A]">{c.investor_name || "Investor"}</div>
                        <div className="text-xs text-[#888]">{c.investor_company || ""} &bull; ${parseInt(c.amount).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${sc.bg} ${sc.text}`}>{c.status.toUpperCase()}</span>
                        {c.status === "pending" && (
                          <button onClick={() => onCommitStatusChange(c.commitment_id, "confirmed")}
                            className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-bold hover:bg-emerald-200">
                            Confirm
                          </button>
                        )}
                        {c.status === "confirmed" && (
                          <button onClick={() => onCommitStatusChange(c.commitment_id, "funded")}
                            className="px-2 py-1 rounded-lg bg-gold/20 text-[#8B6914] text-[10px] font-bold hover:bg-gold/30">
                            Mark Funded
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default InvestorsAdminPanel;
